'use client'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import TenantLogo from '@/components/TenantLogo'
import { useTenant } from '@/contexts/TenantContext'
import { PLATFORM } from '@/lib/branding'

export default function PrivacyPage() {
  const { tenant } = useTenant()
  const businessName = tenant?.name || 'el negocio'

  return (
    <div className="min-h-screen pb-safe">
      <header className="sticky top-0 z-50 glass-effect">
        <div className="container-mobile py-2 flex items-center justify-between">
          <TenantLogo size={32} showName={true} />
          <Link 
            href="/" 
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-light hover:bg-surface transition-all border border-white/10 hover:border-primary/50"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold text-sm">Volver</span>
          </Link>
        </div>
      </header>

      <main className="container-mobile lg:max-w-4xl py-2">
        <div className="card p-8 space-y-6">
          <h1 className="text-3xl font-bold mb-6">Política de Privacidad de {businessName}</h1>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Información General</h2>
            <p className="text-text-secondary leading-relaxed">
              Esta Política de Privacidad explica cómo {businessName} recopila, utiliza y protege tu información personal 
              cuando utilizas nuestra plataforma de reservas en línea y servicios de mensajería WhatsApp.
            </p>
            <p className="text-text-secondary leading-relaxed mt-2">
              Al usar nuestros servicios, aceptas los términos de esta política y nos autorizas a utilizar tus datos personales 
              únicamente para los fines descritos aquí.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Datos Recopilados</h2>
            <p className="text-text-secondary leading-relaxed mb-2">Recopilamos únicamente los siguientes datos:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-text-secondary">
              <li>Tu nombre</li>
              <li>Tu número de teléfono</li>
              <li>Tu correo electrónico (si lo proporcionas)</li>
              <li>Historial de tus reservas</li>
              <li>Tus preferencias de servicio y barbero</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Para Qué Usamos Tus Datos</h2>
            <p className="text-text-secondary leading-relaxed mb-2">Utilizamos tu información únicamente para:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-text-secondary">
              <li>Gestionar y confirmar tus reservas</li>
              <li>Enviarte recordatorios de tus citas por WhatsApp</li>
              <li>Comunicarte cambios o cancelaciones</li>
              <li>Mejorar la calidad de nuestro servicio</li>
              <li>Análisis estadístico interno (datos anónimos)</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-2">
              No usaremos tus datos para ningún otro fin sin tu autorización.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Mensajes por WhatsApp</h2>
            <p className="text-text-secondary leading-relaxed">
              Al hacer una reserva, aceptas recibir mensajes de WhatsApp sobre tus citas: confirmaciones, recordatorios 
              y notificaciones de cambios. Solo te escribiremos al número que nos proporciones.
            </p>
            <p className="text-text-secondary leading-relaxed mt-2">
              Puedes dejar de recibir estos mensajes en cualquier momento contactándonos directamente o respondiendo 
              al WhatsApp pidiendo que dejemos de enviarte mensajes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Cómo Protegemos Tus Datos</h2>
            <p className="text-text-secondary leading-relaxed">
              Tomamos medidas de seguridad para proteger tu información contra accesos no autorizados. Tus datos se 
              almacenan en servidores seguros con cifrado y solo el personal autorizado tiene acceso.
            </p>
            <p className="text-text-secondary leading-relaxed mt-2">
              Mantenemos tu información confidencial y no la compartimos con terceros, excepto cuando sea necesario 
              para darte el servicio o cuando lo requiera la ley.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. No Vendemos Ni Compartimos Tus Datos</h2>
            <p className="text-text-secondary leading-relaxed">
              Nunca venderemos, alquilaremos ni compartiremos tu información con terceros para fines comerciales o 
              publicitarios. Tu información es solo para uso interno de la barbería.
            </p>
            <p className="text-text-secondary leading-relaxed mt-2">
              Si necesitamos usar servicios externos (como WhatsApp para mensajes), estos proveedores están obligados 
              a mantener tu información confidencial y segura.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Tus Derechos</h2>
            <p className="text-text-secondary leading-relaxed mb-2">Tienes derecho a:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-text-secondary">
              <li>Ver qué datos tuyos tenemos guardados</li>
              <li>Corregir información incorrecta</li>
              <li>Pedir que eliminemos tus datos</li>
              <li>Retirar tu autorización para usar tus datos</li>
              <li>Oponerte a ciertos usos de tu información</li>
              <li>Obtener una copia de tus datos</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-2">
              Para ejercer cualquiera de estos derechos, simplemente contáctanos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Cuánto Tiempo Guardamos Tus Datos</h2>
            <p className="text-text-secondary leading-relaxed">
              Guardamos tu información solo mientras sea necesario para brindarte el servicio. Si nos pides que 
              eliminemos tus datos, lo haremos de forma segura de todos nuestros sistemas.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Enlaces a Otros Sitios</h2>
            <p className="text-text-secondary leading-relaxed">
              Nuestra plataforma puede tener enlaces a redes sociales u otros sitios web. No somos responsables 
              de la privacidad ni el contenido de esos sitios. Te recomendamos revisar sus políticas de privacidad.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Cambios en Esta Política</h2>
            <p className="text-text-secondary leading-relaxed">
              Podemos actualizar esta política cuando sea necesario. Los cambios se publicarán aquí y entrarán 
              en vigor inmediatamente. Te recomendamos revisar esta política de vez en cuando.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Cumplimiento Legal</h2>
            <p className="text-text-secondary leading-relaxed">
              Esta política cumple con la Ley de Protección de Datos Personales (Ley 18.331 de Uruguay) y demás 
              regulaciones aplicables. Nos comprometemos a respetar todas las leyes de protección de datos vigentes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contacto</h2>
            <p className="text-text-secondary leading-relaxed">
              Si tienes preguntas sobre esta política o sobre cómo manejamos tu información, contáctanos por email
              a {PLATFORM.supportEmail} o a través de los canales disponibles en la plataforma (WhatsApp, teléfono, o en persona).
            </p>
          </section>

          <div className="pt-6 border-t border-white/10 text-xs text-text-muted">
            <p className="mt-1">Core UY - Todos los derechos reservados</p>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5 py-2 mt-12">
        <div className="container-mobile text-center">
          <a
            href={PLATFORM.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors text-sm"
          >
            <span>Desarrollado por</span>
            <span className="font-semibold">{PLATFORM.name}</span>
          </a>
        </div>
      </footer>
    </div>
  )
}
